/**
 * Pure fit-to-width layout for DataGrid columns, kept free of React/DOM so
 * the distribution rules are unit-testable (same split as columnReorder.ts).
 *
 * The "roomy + scroll" policy (chosen over AG's literal shrink-to-fit,
 * whose one-shot timing on the old grid produced comfortable widths in
 * practice): auto-layout only ever GROWS columns to fill the viewport —
 * 1. columns with `flex` absorb the leftover width proportionally to their
 *    weight (AG `initialFlex` — the samples grid's text columns);
 * 2. with no flex columns visible, every column scales up proportionally
 *    from its declared size (AG `autoSizeStrategy: fitGridWidth` — the
 *    log list), capped at `maxSize`.
 * A column is never auto-compressed below its declared width; when the
 * declared widths overflow the viewport the grid scrolls horizontally.
 *
 * User-resized widths (`overrides`) always win and never redistribute.
 *
 * Every width, override or declared, is first clamped to `[minSize,
 * maxSize]` exactly as TanStack's `column.getSize()` clamps what renders;
 * otherwise a stored width outside the current def's bounds (e.g. a
 * compact-mode score width after compact scores is turned off) makes the
 * fit disagree with the rendered total.
 */

export interface FitColumn {
  id: string;
  size?: number;
  minSize?: number;
  maxSize?: number;
  /** Flex weight; when set the column absorbs leftover width. */
  flex?: number;
}

const kDefaultWidth = 150;
/** TanStack's built-in `minSize` for a def that leaves it unset. */
const kDefaultMinSize = 20;

const hi = (c: FitColumn): number => c.maxSize ?? Infinity;
/** Mirrors TanStack's `column_getSize` clamp: min first, so `maxSize` wins
 *  when the two conflict. */
const renderedWidth = (w: number, c: FitColumn): number =>
  Math.min(Math.max(c.minSize ?? kDefaultMinSize, w), hi(c));
const baseWidth = (c: FitColumn): number =>
  renderedWidth(c.size ?? c.minSize ?? kDefaultWidth, c);
const clampWidth = (w: number, c: FitColumn): number =>
  Math.min(Math.max(w, baseWidth(c)), hi(c));

/**
 * Resolve the width of every visible column for the given available width.
 * `availableWidth <= 0` means "not measured yet" — base sizes pass through
 * so the first paint has sane widths until the container reports in.
 */
export function resolveColumnWidths(
  columns: readonly FitColumn[],
  availableWidth: number,
  overrides: Readonly<Record<string, number>>
): Record<string, number> {
  const widths: Record<string, number> = {};
  for (const c of columns) {
    const override = overrides[c.id];
    widths[c.id] =
      override === undefined ? baseWidth(c) : renderedWidth(override, c);
  }
  if (availableWidth <= 0) return widths;

  const fitted = columns.filter((c) => overrides[c.id] === undefined);
  if (fitted.length === 0) return widths;
  const overriddenTotal = columns.reduce(
    (sum, c) => (overrides[c.id] === undefined ? sum : sum + widths[c.id]!),
    0
  );

  // Stage 1: flex columns absorb the width left over by everything else.
  const flexCols = fitted.filter((c) => (c.flex ?? 0) > 0);
  if (flexCols.length > 0) {
    const flexSet = new Set(flexCols);
    const nonFlexTotal = columns.reduce(
      (sum, c) => (flexSet.has(c) ? sum : sum + widths[c.id]!),
      0
    );
    distribute(
      flexCols,
      availableWidth - nonFlexTotal,
      (c) => c.flex ?? 0,
      widths
    );
  }

  // Stage 2: grow every fitted column proportionally from its current
  // width until the total fills the available width. Skipped when the
  // columns already fill (or overflow — no shrinking) the viewport.
  const total = columns.reduce((sum, c) => sum + widths[c.id]!, 0);
  if (total < availableWidth) {
    distribute(
      fitted,
      availableWidth - overriddenTotal,
      (c) => widths[c.id]!,
      widths
    );
  }
  return widths;
}

/** Split `target` px across `cols` proportionally to weight, clamping each
 *  column between its declared width and `maxSize`, and redistributing what
 *  clamping frees up. Widths land in `out` as integers whose total never
 *  exceeds `target` (except when the declared widths alone overflow it —
 *  then the grid scrolls). */
function distribute(
  cols: readonly FitColumn[],
  target: number,
  weightOf: (c: FitColumn) => number,
  out: Record<string, number>
): void {
  const exact = new Map<string, number>();
  const floorTotal = cols.reduce((sum, c) => sum + baseWidth(c), 0);
  if (target <= floorTotal) {
    for (const c of cols) out[c.id] = baseWidth(c);
    return;
  }

  let active = [...cols];
  let remaining = target;
  while (active.length > 0) {
    const weightSum = active.reduce((sum, c) => sum + weightOf(c), 0);
    if (weightSum <= 0) {
      for (const c of active) exact.set(c.id, baseWidth(c));
      break;
    }
    const clamped: FitColumn[] = [];
    for (const c of active) {
      const ideal = (remaining * weightOf(c)) / weightSum;
      const width = clampWidth(ideal, c);
      exact.set(c.id, width);
      if (width !== ideal) clamped.push(c);
    }
    // No clamping (or nothing left to rebalance against): shares are final.
    if (clamped.length === 0 || clamped.length === active.length) break;
    for (const c of clamped) remaining -= exact.get(c.id)!;
    const clampedSet = new Set(clamped);
    active = active.filter((c) => !clampedSet.has(c));
  }

  // Integers: floor the exact shares, then hand the lost pixels back (where
  // the max allows) so the total lands flush against the target instead of
  // a few px short.
  let sum = 0;
  for (const c of cols) {
    const w = Math.floor(exact.get(c.id)!);
    out[c.id] = w;
    sum += w;
  }
  let spare = Math.floor(target) - sum;
  for (const c of cols) {
    if (spare <= 0) break;
    if (out[c.id]! + 1 <= hi(c)) {
      out[c.id]! += 1;
      spare -= 1;
    }
  }
}
